import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styles from './PersonnelPage.module.css'
import pa from './ProductActivityPage.module.css'
import { useAuth } from '../contexts/AuthContext'
import { apiJson, getActiveTenantId } from '../lib/api'
import { actingFor, writeActAs } from '../lib/actAs'
import { can, hasPower, useAccess } from '../lib/access'
import { useHelpPref } from '../lib/preferences'
import { PEOPLE_TABS, PairTabs } from '../components/PairTabs'
import { PageHeader, PageBody, headerStyles } from '../components/PageHeader'
import { PersonnelSettingsModal } from './personnel/PersonnelSettingsModal'
import { MergeDialog } from './personnel/MergeDialog'
import { BulkEditPanel } from './personnel/BulkEditPanel'
import { ImportPanel } from './personnel/ImportPanel'
import { PersonRecordPanel, fullName, initialsOf, withheldBy, type ClientApp, type Person } from './personnel/PersonRecordPanel'
import { PersonAvatar } from './personnel/PersonAvatar'
import { InviteDialog, type InviteResult } from './personnel/InviteDialog'
import { fileTask, useTasks } from '../components/TaskCentre'
import { useLayer } from '../components/layers'
import { PendingButton, useSlow, SkeletonTableRows, Failed } from '../components/pending'
import { AsAt } from '../components/AsAt'
import { useRefetch } from '../lib/connection'
import { workbook, XLSX_TYPE, type CellValue } from './batches/xlsx'
import { dmy, money } from './wages/format'
import type { LabelShortcut } from './labels/model'
import { openableRow } from '../lib/openableRow'

// A sign-in account (041). One with no person record of its own is shown under
// System users with nothing to edit; one with a person adds its invite state
// (164) to that person's row.
interface Account {
  id: string
  email: string
  permission_set: string
  human_id: string | null
  archived: boolean
  name: string | null
  has_signed_in?: boolean
  invite_status?: 'pending' | 'expired' | null
  invite_expires_at?: string | null
  client_apps?: ClientApp[]
}

const inviteWhen = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })

type Tab = 'field' | 'system' | 'archived'
const TAB_LABEL: Record<Tab, string> = { field: 'Field workers', system: 'System users', archived: 'Archived' }
type SortKey = 'name' | 'employee_id' | 'rate' | 'contact' | 'last_active'
const PAGE_SIZE = 25

// Three lists, one record type (§7b). A person linked to a sign-in account is
// a system user, and is counted there once even if they are also paid hourly.
const tabOf = (p: Person): Tab => (p.archived ? 'archived' : p.membership_id ? 'system' : 'field')

// The five parts of the Personnel help panel (handoff §7b).
const HELP: [string, string][] = [
  [
    'Three lists, one record type',
    'Field workers are the people who pick, supervise and receipt. System users are the people who sign in to CONTROL. Archived holds anyone no longer working for you. Nothing is deleted, so history stays intact.',
  ],
  [
    'How people arrive here',
    'Added by hand with Add worker, or self-signed-up in the Dataphyll Crew App from an induction email or a QR code, both issued from Teams. Everyone lands in the same list, and a self-signup carries the phone marker beside their name.',
  ],
  [
    'Rates are set here',
    'The rate lives on the person’s record and drives everything wages calculates. A new rate starts a new period and never changes the ones before it. A past rate that was recorded wrongly can be corrected on the person’s record: the old rate and dates are kept, and the correction is refused if it reaches a locked day. The cog sets the default rate for anyone with none of their own.',
  ],
  [
    'Merging duplicates',
    'Two live people with the same name are flagged Dupe?. Open the flag to merge them, or to record that they are not the same person. Or tick exactly two records and press Merge profiles. Either way you choose which record survives; later picks, timesheets and tags move onto it and the other becomes its alias. Days already sent to payroll stay with the archived record. The merge is listed on the surviving record, with Undo.',
  ],
  [
    'One person or many',
    'Click a row to edit one person. Tick several and choose Bulk edit: rate, group, team, roles, wages and archive apply to everyone selected. Fields left unchanged are left alone. A new rate is written as its own version on each person. Archive in the selection bar asks first and deletes nothing.',
  ],
]

// What search reads: name, email, employee ID, group and role (handoff 83).
const haystack = (p: Person) =>
  [fullName(p), p.first_name, p.last_name, p.email, p.employee_id, p.group_name, p.permission_set, ...(p.job_roles || [])]
    .filter(Boolean)
    .join('\n')
    .toLowerCase()

const rateOf = (p: Person) => (p.hourly_rate == null ? null : Number(p.hourly_rate))

const sortValue = (p: Person, key: SortKey): string | number | null => {
  if (key === 'name') return fullName(p).toLowerCase()
  if (key === 'employee_id') return p.employee_id ? p.employee_id.toLowerCase() : null
  if (key === 'rate') return rateOf(p)
  if (key === 'contact') return (p.email || p.phone || '').toLowerCase() || null
  return p.last_active || null
}

// Empty values sort last whichever way the column runs; ties fall back to
// the name so the order is stable.
export function sortPeople(list: Person[], key: SortKey, dir: 1 | -1): Person[] {
  return [...list].sort((a, b) => {
    const x = sortValue(a, key)
    const y = sortValue(b, key)
    if (x == null || y == null) {
      if (x != null) return -1
      if (y != null) return 1
    } else {
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true })
      if (c) return c * dir
    }
    return fullName(a).localeCompare(fullName(b))
  })
}

// A spreadsheet runs a cell that starts with = + - or @, so those are
// written as text.
const cell = (v: unknown) => {
  let s = v === null || v === undefined ? '' : String(v)
  if (/^[=+\-@]/.test(s)) s = "'" + s
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

// The current view as rows: the list's own columns, with the name's second
// line (roles, group, default team, permission set) spelled out.
// Without pay_data the rate columns are left out (approved data boundary,
// D1 point 7): the server sent no rates to put in them.
export function personnelRows(people: Person[], accounts: Account[] = [], pay = true): CellValue[][] {
  const head = ['Name', 'Roles', 'Group', 'Default team', 'Permission set', 'Employee ID', 'Hourly rate', 'Next rate', 'Next rate from', 'Email', 'Phone', 'Last active', 'List']
  const keep = (r: CellValue[]) => (pay ? r : [...r.slice(0, 6), ...r.slice(9)])
  const rows: CellValue[][] = [keep(head)]
  for (const p of people) {
    rows.push(keep([
      fullName(p),
      (p.job_roles || []).join(', '),
      p.group_name || '',
      p.default_team_name || '',
      p.membership_id ? p.permission_set || '' : '',
      p.employee_id || '',
      rateOf(p),
      p.next_rate == null ? null : Number(p.next_rate),
      p.next_rate_from || '',
      p.email || '',
      p.phone || '',
      p.last_active ? String(p.last_active).slice(0, 10) : '',
      TAB_LABEL[tabOf(p)],
    ]))
  }
  for (const a of accounts) rows.push(keep([a.name || a.email, '', '', '', a.permission_set, '', null, null, '', a.email, '', '', TAB_LABEL.system]))
  return rows
}
export const personnelCsv = (rows: CellValue[][]) => rows.map((r) => r.map(cell).join(',')).join('\r\n') + '\r\n'

// How a sign-in signs in, as Firebase names it (migration 139).
const SIGN_IN_NAMES: Record<string, string> = {
  'google.com': 'Google',
  'microsoft.com': 'Microsoft',
  'facebook.com': 'Meta (Facebook)',
  'apple.com': 'Apple',
  password: 'email and password',
  emailLink: 'an emailed link',
  phone: 'phone',
}
const signInName = (provider: string) => SIGN_IN_NAMES[provider] || provider

function SignInGlyph({ provider }: { provider: string }) {
  const svg = { width: 12, height: 12, viewBox: '0 0 24 24', 'aria-hidden': true } as const
  if (provider === 'google.com')
    return (
      <svg {...svg}>
        <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.2-2.1 3.5-5.1 3.5-8.8z" />
        <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1A12 12 0 0 0 12 24z" />
        <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.3a12 12 0 0 0 0 10.8l4-3.1z" />
        <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z" />
      </svg>
    )
  if (provider === 'microsoft.com')
    return (
      <svg {...svg}>
        <path fill="#F25022" d="M1 1h10.5v10.5H1z" />
        <path fill="#7FBA00" d="M12.5 1H23v10.5H12.5z" />
        <path fill="#00A4EF" d="M1 12.5h10.5V23H1z" />
        <path fill="#FFB900" d="M12.5 12.5H23V23H12.5z" />
      </svg>
    )
  if (provider === 'facebook.com')
    return (
      <svg {...svg}>
        <circle cx="12" cy="12" r="11" fill="#1877F2" />
        <path fill="#fff" d="M13.4 19.5v-6h2l.3-2.4h-2.3V9.6c0-.7.2-1.2 1.2-1.2h1.2V6.3a16 16 0 0 0-1.8-.1c-1.8 0-3 1.1-3 3.1v1.8h-2v2.4h2v6z" />
      </svg>
    )
  if (provider === 'apple.com')
    return (
      <svg {...svg}>
        <path fill="currentColor" d="M16.4 12.6c0-2.4 2-3.5 2-3.6a4.3 4.3 0 0 0-3.4-1.8c-1.4-.2-2.8.8-3.5.8-.7 0-1.8-.8-3-.8a4.5 4.5 0 0 0-3.8 2.3c-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.3 0 2.1-1.1 2.8-2.3.9-1.3 1.3-2.5 1.3-2.6 0 0-2.5-1-2.5-3.6zM14.1 5.6c.6-.8 1.1-1.8 1-2.9-.9 0-2.1.6-2.7 1.4-.6.7-1.1 1.8-1 2.8 1 .1 2.1-.5 2.7-1.3z" />
      </svg>
    )
  return (
    <svg {...svg} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 6.5 8.5 6.5 8.5-6.5" />
    </svg>
  )
}

// The contact cell: a sign-in that has signed in here shows its email in a badge led by how it signs in ("oh,
// Christoph uses Google"). A contact email that differs from it stays above as plain text.
function ContactCell({ p }: { p: Person }) {
  const methods = p.sign_in_methods || []
  const signInEmail = p.sign_in_email || p.email
  const withheld = withheldBy(p)
  if (withheld && !signInEmail) {
    const how = `Signs in to Dataphyll Crew with ${(p.crew_sign_ins || []).map(signInName).join(' and ')}, which withheld their email`
    return (
      <div className={styles.contact}>
        <span className={styles.signInBadge} title={how} aria-label={`Email withheld (${withheld}). ${how}`}>
          {(p.crew_sign_ins || []).map((m) => (
            <SignInGlyph key={m} provider={m} />
          ))}
          <span className={styles.muted}>Withheld ({withheld})</span>
        </span>
        <div className={styles.contactSub}>{p.phone || ''}</div>
      </div>
    )
  }
  if (!methods.length || !signInEmail) {
    return (
      <div className={styles.contact}>
        <div>{p.email || '-'}</div>
        <div className={styles.contactSub}>{p.phone || ''}</div>
      </div>
    )
  }
  const how = `Signs in with ${methods.map(signInName).join(' and ')}`
  return (
    <div className={styles.contact}>
      {p.email && p.email.toLowerCase() !== signInEmail.toLowerCase() && <div>{p.email}</div>}
      <span className={styles.signInBadge} title={how} aria-label={`${signInEmail}. ${how}`}>
        {methods.map((m) => (
          <SignInGlyph key={m} provider={m} />
        ))}
        <span>{signInEmail}</span>
      </span>
      <div className={styles.contactSub}>{p.phone || ''}</div>
    </div>
  )
}

// The apps someone uses (166): one badge per app, for the platform they used it on last, its icon and the version it
// said it was; the others are in its tooltip. A row from a build too old to say (Crew 1.0.0 (6)) only shows when
// nothing newer has placed that app.
const PLATFORM_NAMES: Record<string, string> = { android: 'Android', ios: 'iPhone', web: 'the web', unknown: 'an older build' }

function PlatformGlyph({ platform }: { platform: string }) {
  const svg = { width: 12, height: 12, viewBox: '0 0 24 24', 'aria-hidden': true } as const
  if (platform === 'android')
    return (
      // The robot's head, its box cropped to it so it stands as tall as the Apple logo beside it.
      <svg {...svg} width={17} viewBox="1.5 6.3 21 13.2" fill="#3DDC84" stroke="#3DDC84">
        <path stroke="none" fillRule="evenodd" d="M2 19a10 10 0 0 1 20 0zM6.6 14.6a1.6 1.6 0 1 0 3.2 0a1.6 1.6 0 1 0-3.2 0zM14.2 14.6a1.6 1.6 0 1 0 3.2 0a1.6 1.6 0 1 0-3.2 0z" />
        <path fill="none" strokeWidth="2" strokeLinecap="round" d="M7.2 11.2 5 7.4M16.8 11.2 19 7.4" />
      </svg>
    )
  if (platform === 'ios') return <SignInGlyph provider="apple.com" />
  if (platform === 'web')
    return (
      <svg {...svg} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
      </svg>
    )
  return (
    <svg {...svg} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="2" width="12" height="20" rx="2" />
      <path d="M11 18h2" />
    </svg>
  )
}

type ShownApp = ClientApp & { others: ClientApp[] }

export function shownClientApps(apps: ClientApp[] | undefined): ShownApp[] {
  const last = (a: ClientApp) => Date.parse(a.last_seen_at) || 0
  const list = (apps || []).slice().sort((a, b) => last(b) - last(a))
  const known = list.filter((a) => a.platform !== 'unknown' || !list.some((b) => b.app === a.app && b.platform !== 'unknown'))
  const shown: ShownApp[] = []
  for (const a of known) {
    const first = shown.find((s) => s.app === a.app)
    if (first) first.others.push(a)
    else shown.push({ ...a, others: [] })
  }
  return shown
}

// Dataphyll's own app version, "20261029A" (codemagic.yaml).
const VERSION_RE = /^\d{8}Z*[A-Y]$/

function ClientAppBadges({ apps }: { apps: ShownApp[] }) {
  return (
    <>
      {apps.map((a) => {
        const name = a.app === 'crew' ? 'Crew' : 'CONTROL'
        // Crew says its version, "20261029A", or a store build number from before versions; CONTROL its release.
        const dated = !!a.version && VERSION_RE.test(a.version)
        const version = a.version ? (a.app === 'crew' ? (dated ? a.version : `v${a.version}`) : a.version.slice(0, 7)) : null
        const what = `${a.app === 'crew' ? 'Dataphyll Crew' : 'CONTROL'} on ${PLATFORM_NAMES[a.platform] || a.platform}`
        const also = a.others.map((o) => `${PLATFORM_NAMES[o.platform] || o.platform} ${new Date(o.last_seen_at).toLocaleDateString()}`)
        const title = `${what}, ${a.version ? (a.app === 'crew' ? `${dated ? 'version' : 'build'} ${a.version}` : `release ${a.version}`) : 'version not known'}. Last used ${new Date(a.last_seen_at).toLocaleString()}${also.length ? `. Before that: ${also.join(', ')}` : ''}`
        return (
          <span key={`${a.app}:${a.platform}`} className={`${styles.connChip} ${styles.appChip} ${a.app === 'crew' ? styles.connCrew : ''}`} title={title} aria-label={title}>
            <PlatformGlyph platform={a.platform} />
            {name}
            {version && <span className={styles.appVersion}>{version}</span>}
          </span>
        )
      })}
    </>
  )
}

// What the person is connected to: Dataphyll Crew, and each payroll provider holding them, with its employee ID.
// Someone added here and connected to nothing shows nothing.
function Connections({ p }: { p: Person }) {
  const links = p.provider_links || []
  const apps = shownClientApps(p.client_apps)
  if (p.origin !== 'worker_app' && links.length === 0 && apps.length === 0) return <span className={styles.muted}>-</span>
  return (
    <div className={styles.connections}>
      <ClientAppBadges apps={apps} />
      {p.origin === 'worker_app' && !apps.some((a) => a.app === 'crew') && (
        <span className={`${styles.connChip} ${styles.connCrew}`} title="Signed themselves up in Dataphyll Crew">
          Crew
        </span>
      )}
      {links.map((l) => (
        <span key={`${l.type}:${l.employee_id}`} className={styles.connChip} title={`${l.name}: employee ${l.employee_id}`}>
          {l.type}
        </span>
      ))}
    </div>
  )
}

// The header checkbox shows a dash when only part of the page is selected.
function PageCheckbox({ checked, partial, onChange, disabled }: { checked: boolean; partial: boolean; onChange: () => void; disabled: boolean }) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = partial
  }, [partial])
  return <input ref={ref} type="checkbox" aria-label="Select every row on this page" checked={checked} disabled={disabled} onChange={onChange} />
}

export function PersonnelPage() {
  const [personnel, setPersonnel] = useState<Person[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  // Track what data key has been loaded; while loading, skeleton rows show (h 3994-4002)
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [asAt, setAsAt] = useState<Date | null>(null)
  const [tab, setTab] = useState<Tab>('field')
  const [panel, setPanel] = useState<{ person: Person | null } | null>(null)
  // D1 as approved: Add worker, archive and merge need Personnel Add, bulk
  // edit needs Edit, rates are for pay_data holders, and the cog's default
  // rate lives in Wages settings. The server refuses each regardless.
  const mine = useAccess()
  const mayAdd = can(mine, 'personnel', 'add')
  const mayEdit = can(mine, 'personnel', 'edit')
  const pay = hasPower(mine, 'pay_data')
  const mayRate = pay && can(mine, 'wages', 'edit')
  // The job roles on offer are managed by an ADMIN (B-1(a)); the server says
  // whether this user may change them, and refuses otherwise.
  const mayCog = mayRate || mayAdd
  const mayImport = mayAdd && pay
  const [cog, setCog] = useState<{ current: number | null } | null>(null)
  const [cogError, setCogError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  // Invite system user: { result } shows a sent invitation (Resend), {} the form.
  const mayInvite = mayAdd && hasPower(mine, 'manage_sign_in')
  const [actAsBusy, setActAsBusy] = useState<string | null>(null)
  const [inviting, setInviting] = useState<{ result?: InviteResult } | null>(null)
  const [inviteBusy, setInviteBusy] = useState<string | null>(null)
  const [withdrawAsk, setWithdrawAsk] = useState<string | null>(null)
  const { startTask } = useTasks()
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 })
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [groupMenu, setGroupMenu] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [roleFilter, setRoleFilter] = useState<string[]>([])
  const [rateFilter, setRateFilter] = useState<'' | 'floor' | 'above'>('')
  // The default rate the rate filter splits on, read from Wages when the popover first opens.
  const [floor, setFloor] = useState<number | null>(null)
  const [exportMenu, setExportMenu] = useState(false)
  const [merging, setMerging] = useState<[Person, Person] | null>(null)
  const [bulk, setBulk] = useState<Person[] | null>(null)
  const [importing, setImporting] = useState(false)
  // Possible duplicates (A-6(a)): live people with the same name, less the
  // pairs someone has said are not the same person. Each id lists the others.
  const [dupes, setDupes] = useState<Map<string, string[]>>(() => new Map())
  const [dupeMenu, setDupeMenu] = useState<string | null>(null)
  const [dupeBusy, setDupeBusy] = useState(false)
  const [dupeError, setDupeError] = useState<string | null>(null)
  // Archive from the selection bar asks first, inline, naming the count (h 3975-4002).
  const [archiveAsk, setArchiveAsk] = useState(false)
  const [archiveBusy, setArchiveBusy] = useState(false)
  const [archiveError, setArchiveError] = useState<string | null>(null)
  const archiveSlow = useSlow(archiveBusy)
  const { user } = useAuth()
  const [help, setHelp] = useHelpPref(user, 'personnel')
  const navigate = useNavigate()
  // ?group=<id> narrows the list to one group's members; Groups links here
  // from "See all N members in Personnel", and the Group pill sets it too.
  const [params, setParams] = useSearchParams()
  const groupFilter = params.get('group')

  const userRef = useRef(user)
  userRef.current = user
  const seq = useRef(0)
  const load = useCallback(
    async (quiet = false) => {
      const u = userRef.current
      if (!u) return
      const n = ++seq.current
      try {
        const data = await apiJson<Person[]>(u, '/api/personnel')
        if (n !== seq.current) return
        setPersonnel(Array.isArray(data) ? data : [])
        setError(null)
        setAsAt(new Date())
      } catch (err) {
        if (n !== seq.current) return
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        // Mark this data key as loaded only if still the latest request (h 3994-4002)
        if (n === seq.current) setLoadedKey('loaded')
      }
      // Accounts only add rows to System users; the page works without them.
      try {
        const a = await apiJson<Account[]>(u, '/api/personnel/system-users')
        if (n !== seq.current) return
        setAccounts(Array.isArray(a) ? a : [])
      } catch {
        setAccounts([])
      }
      // The Dupe? flags are a suggestion; the list works without them.
      try {
        const d = await apiJson<{ pairs?: { a: string; b: string }[] }>(u, '/api/personnel/duplicates')
        if (n !== seq.current) return
        const m = new Map<string, string[]>()
        for (const { a, b } of Array.isArray(d?.pairs) ? d.pairs : []) {
          m.set(a, [...(m.get(a) || []), b])
          m.set(b, [...(m.get(b) || []), a])
        }
        setDupes(m)
      } catch {
        setDupes(new Map())
      }
    },
    []
  )
  const loading = loadedKey !== 'loaded'

  useEffect(() => {
    if (user) load()
  }, [user])
  // h 4167-4171: it fetches again on window focus and on reconnect.
  useRefetch(() => load(true))

  // Global search links here (h 465-485): ?q= fills the search box, and
  // ?person= opens that record on its own list once the list has loaded.
  // Both are taken once and dropped from the address.
  const linkedQ = params.get('q')
  const linkedPerson = params.get('person')
  useEffect(() => {
    if (linkedQ === null && linkedPerson === null) return
    if (linkedPerson !== null && loading) return
    if (linkedQ !== null) setQ(linkedQ)
    const p = linkedPerson ? personnel.find((x) => x.id === linkedPerson) : undefined
    if (p) {
      setTab(tabOf(p))
      setPanel({ person: p })
    }
    const next = new URLSearchParams(params)
    next.delete('q')
    next.delete('person')
    setParams(next, { replace: true })
  }, [linkedQ, linkedPerson, loading])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 4000)
    return () => clearTimeout(t)
  }, [toast])

  // Each open menu and the inline archive question is a layer (h 425-458);
  // the cog, merge, bulk and record panels register their own.
  useLayer(groupMenu, () => setGroupMenu(false))
  useLayer(filtersOpen, () => setFiltersOpen(false))
  useLayer(exportMenu, () => setExportMenu(false))
  useLayer(!!dupeMenu, () => !dupeBusy && setDupeMenu(null), { busy: dupeBusy })
  useLayer(archiveAsk, () => !archiveBusy && setArchiveAsk(false), { busy: archiveBusy })

  // Switching tab or filter clears the selection rather than silently
  // carrying it, and goes back to the first page (handoff 961-962).
  useEffect(() => {
    setSelected(new Set())
    setPage(0)
  }, [tab, groupFilter, q, roleFilter, rateFilter])

  const toggleFilters = async () => {
    const opening = !filtersOpen
    setFiltersOpen(opening)
    if (!opening || !pay || floor != null) return
    try {
      const s = await apiJson<{ default_min_rate: number | null }>(user, '/api/wages/settings')
      setFloor(s.default_min_rate)
    } catch {
      // Without the default, "at the floor" is just the people with no rate of their own.
    }
  }

  // A changed selection is a different question: the archive ask and its
  // refusal belong to the selection they were about.
  useEffect(() => {
    setArchiveAsk(false)
    setArchiveError(null)
  }, [selected])

  // The current default is read when the cog opens, so the field always
  // starts from what Wages is assessing against now.
  const openCog = async () => {
    setCogError(null)
    if (!mayRate) {
      setCog({ current: null })
      return
    }
    try {
      const s = await apiJson<{ default_min_rate: number | null }>(user, '/api/wages/settings')
      setCog({ current: s.default_min_rate })
    } catch (err) {
      setCogError(err instanceof Error ? err.message : 'Could not load Personnel settings')
    }
  }

  // A list that never loaded has nothing to show but what failed, and a way to try again. Once it has loaded, a
  // refetch that fails (on focus or reconnect) must not take the list, or a record open over it, away: the page stays
  // and the failure is a banner above it (below).
  if (error && !loading && !asAt) {
    return (
      <div className={styles.container}>
        <h1>Personnel</h1>
        <div className={styles.error} role="alert">
          <p>{error}</p>
          <button type="button" className={pa.ghostBtn} onClick={() => load()}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Archived workers draw no wages and system users need no rate, so neither
  // is counted as needing the default.
  const withoutRate = personnel.filter((p) => tabOf(p) === 'field' && p.hourly_rate == null).length
  const accountsOnly = accounts.filter((a) => !a.human_id && !a.archived)
  const accountById = new Map(accounts.map((a) => [a.id, a]))

  // An invitation not yet taken up: its state, and (for whoever may invite) Resend and Withdraw. The row
  // itself opens the person, so the buttons keep their clicks to themselves.
  const resendInvite = async (id: string) => {
    setInviteBusy(id)
    try {
      const r = await apiJson<InviteResult>(user, `/api/personnel/invites/${id}/resend`, { method: 'POST' })
      setInviting({ result: r })
      load(true)
    } catch (e: any) {
      setToast(e?.message || 'Could not send the invitation again')
    } finally {
      setInviteBusy(null)
    }
  }
  const withdrawInvite = async (a: Account) => {
    setInviteBusy(a.id)
    try {
      await apiJson(user, `/api/personnel/invites/${a.id}`, { method: 'DELETE' })
      setToast(`Invitation to ${a.email} withdrawn`)
      load(true)
    } catch (e: any) {
      setToast(e?.message || 'Could not withdraw the invitation')
    } finally {
      setInviteBusy(null)
      setWithdrawAsk(null)
    }
  }
  const inviteBits = (a: Account | undefined) => {
    if (!a || a.archived || a.has_signed_in || !a.invite_status || !a.invite_expires_at) return null
    const busyHere = inviteBusy === a.id
    const stop = (e: React.SyntheticEvent) => e.stopPropagation()
    return (
      <>
        {a.invite_status === 'pending' ? (
          <span className={styles.inviteChip}>Invited · expires {inviteWhen(a.invite_expires_at)}</span>
        ) : (
          <span className={styles.inviteExpired}>Invite expired</span>
        )}
        {mayInvite && (
          <>
            <button type="button" className={styles.inviteAct} disabled={busyHere} onClick={(e) => (stop(e), resendInvite(a.id))} onKeyDown={stop}>
              {a.invite_status === 'pending' ? 'Resend' : 'Send again'}
            </button>
            {' · '}
            <button
              type="button"
              className={styles.inviteAct}
              disabled={busyHere}
              onClick={(e) => (stop(e), withdrawAsk === a.id ? withdrawInvite(a) : setWithdrawAsk(a.id))}
              onKeyDown={stop}
              onBlur={() => withdrawAsk === a.id && !busyHere && setWithdrawAsk(null)}
            >
              {withdrawAsk === a.id ? 'Withdraw: sure?' : 'Withdraw'}
            </button>
          </>
        )}
      </>
    )
  }
  // A SYSADMIN acting as a system user (routes/impersonation.js): offered on anyone signed in here who is not a
  // SYSADMIN and not you. The start is recorded, then the screen loads again as them (lib/actAs.ts).
  const actAsBits = (a: Account | undefined) => {
    if (!a || a.archived || !a.has_signed_in || a.permission_set === 'SYSADMIN' || mine?.role !== 'SYSADMIN' || actingFor(user?.uid, getActiveTenantId())) return null
    if (user?.email && a.email.toLowerCase() === user.email.toLowerCase()) return null
    const stop = (e: React.SyntheticEvent) => e.stopPropagation()
    return (
      <button type="button" className={styles.inviteAct} disabled={actAsBusy === a.id} onClick={(e) => (stop(e), startActAs(a))} onKeyDown={stop}>
        {actAsBusy === a.id ? 'Starting…' : 'Act as'}
      </button>
    )
  }
  const startActAs = async (a: Account) => {
    const tenantId = getActiveTenantId()
    if (!user || !tenantId) return
    setActAsBusy(a.id)
    try {
      const r = await apiJson<{ acting_as: { membership_id: string; email: string; name: string | null; permission_set: string } }>(user, '/api/impersonation', {
        body: { membershipId: a.id },
      })
      writeActAs({ uid: user.uid, tenantId, membershipId: r.acting_as.membership_id, email: r.acting_as.email, name: r.acting_as.name, permission_set: r.acting_as.permission_set })
      window.location.assign('/')
    } catch (e: any) {
      setToast(e?.message || 'Could not act as them')
      setActAsBusy(null)
    }
  }
  const counts: Record<Tab, number> = {
    field: personnel.filter((p) => tabOf(p) === 'field').length,
    system: personnel.filter((p) => tabOf(p) === 'system').length + accountsOnly.length,
    archived: personnel.filter((p) => tabOf(p) === 'archived').length,
  }
  // A group's members are everyone mapped to it, whichever list they sit in,
  // so the count matches the group's own member count.
  const inView = groupFilter ? personnel.filter((p) => p.group_id === groupFilter) : personnel.filter((p) => tabOf(p) === tab)
  const accountsInView = !groupFilter && tab === 'system' ? accountsOnly : []
  const filterName = groupFilter ? inView.find((p) => p.group_name)?.group_name || 'this group' : null
  // The groups people are in, for the pill: a group nobody is in has nothing
  // to show, and Groups is where empty ones are managed.
  const groupOptions = Array.from(new Map(personnel.filter((p) => p.group_id).map((p) => [p.group_id as string, p.group_name || 'Unnamed group'])).entries()).sort((a, b) =>
    a[1].localeCompare(b[1])
  )

  const needle = q.trim().toLowerCase()
  // The Filters popover (V4 ppFilters): any of the ticked roles, and one side of the default rate. "At the floor" is
  // anyone on the default, by having no rate of their own or one no higher than it.
  const roleOptions = Array.from(new Set(personnel.flatMap((p) => p.job_roles || []))).sort((a, b) => a.localeCompare(b))
  const filterCount = roleFilter.length + (rateFilter ? 1 : 0)
  const onFloor = (p: Person) => p.hourly_rate == null || (floor != null && Number(p.hourly_rate) <= floor)
  const passesFilters = (p: Person) =>
    (!roleFilter.length || (p.job_roles || []).some((r) => roleFilter.includes(r))) &&
    (!rateFilter || (rateFilter === 'floor' ? onFloor(p) : !onFloor(p)))
  const matching = sortPeople(
    inView.filter((p) => (!needle || haystack(p).includes(needle)) && passesFilters(p)),
    sort.key,
    sort.dir
  )
  // An account with no person record has no roles or rate, so a filter leaves it out.
  const matchingAccounts = filterCount
    ? []
    : accountsInView.filter((a) => !needle || [a.name, a.email, a.permission_set].some((v) => v && v.toLowerCase().includes(needle)))
  const total = inView.length + accountsInView.length
  const matchCount = matching.length + matchingAccounts.length
  const pages = Math.max(1, Math.ceil(matching.length / PAGE_SIZE))
  const at = Math.min(page, pages - 1)
  const shown = matching.slice(at * PAGE_SIZE, (at + 1) * PAGE_SIZE)
  // Accounts without a person record come after the people and cannot be
  // selected: there is no record of theirs for any action to change.
  const extraAccounts = at === pages - 1 ? matchingAccounts : []

  const pageIds = shown.map((p) => p.id)
  const pageSelected = pageIds.filter((id) => selected.has(id)).length
  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  const togglePage = () =>
    setSelected((s) => {
      const n = new Set(s)
      if (pageSelected === pageIds.length) pageIds.forEach((id) => n.delete(id))
      else pageIds.forEach((id) => n.add(id))
      return n
    })
  const selectedPeople = matching.filter((p) => selected.has(p.id))
  // Print QR labels (h 855) is the one Personnel › Label printing shortcut
  // (h 4602-4605): a selection arrives as a Worker ID batch, archived people
  // left behind, and with nobody ticked the batch starts empty. What may be
  // generated is Label printing's own check, on that page and the server.
  const onlyArchived = selectedPeople.length > 0 && selectedPeople.every((p) => p.archived)
  const labelable = selectedPeople.filter((p) => !p.archived)
  const printLabels = () => {
    const state: LabelShortcut = { labelWorkers: labelable.map((p) => p.id) }
    navigate('/label-printing', { state })
  }

  // One person opens their own record: "bulk edit" is never the words over a
  // single person (handoff 876-881).
  const editSelection = () => {
    if (selectedPeople.length === 1) setPanel({ person: selectedPeople[0] })
    else setBulk(selectedPeople)
  }
  const archiveSelection = async () => {
    if (archiveBusy) return
    setArchiveBusy(true)
    setArchiveError(null)
    try {
      const r = await apiJson<{ updated: number; sign_in_removed?: number }>(user, '/api/personnel/bulk', { body: { ids: selectedPeople.map((p) => p.id), archived: true } })
      setArchiveAsk(false)
      setSelected(new Set())
      // Archiving removes CONTROL sign-in here in the same step (C-1(a)).
      const off = r.sign_in_removed || 0
      setToast(`${r.updated} ${r.updated === 1 ? 'record' : 'records'} archived${off ? ` · CONTROL sign-in removed for ${off === 1 ? '1 person' : `${off} people`}` : ''}`)
      load(true)
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Nobody was archived')
      setArchiveAsk(false)
    } finally {
      setArchiveBusy(false)
    }
  }

  // "Not the same person" (A-6(a)): recorded for this pair only, so the
  // pair stops being suggested; anyone else with the name still is.
  const dismissDupe = async (a: Person, b: Person) => {
    if (dupeBusy) return
    setDupeBusy(true)
    setDupeError(null)
    try {
      await apiJson(user, '/api/personnel/duplicates/dismiss', { body: { a: a.id, b: b.id } })
      setDupeMenu(null)
      setToast(`${fullName(a)} and ${fullName(b)} are recorded as different people`)
      load(true)
    } catch (err) {
      setDupeError(err instanceof Error ? err.message : 'That was not recorded')
    } finally {
      setDupeBusy(false)
    }
  }

  const sortBy = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }))
  const th = (key: SortKey, label: string, className?: string) => (
    <th className={className} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={styles.sortBtn} onClick={() => sortBy(key)}>
        {label}
        <span className={styles.sortMark} aria-hidden="true">
          {sort.key === key ? (sort.dir === 1 ? '▲' : '▼') : ''}
        </span>
      </button>
    </th>
  )

  const viewName = groupFilter ? (filterName || 'group').toLowerCase().replace(/[^a-z0-9]+/g, '-') : tab === 'field' ? 'field-workers' : tab === 'system' ? 'system-users' : 'archived'
  const exportAs = (kind: 'csv' | 'xlsx') => {
    setExportMenu(false)
    const rows = personnelRows(matching, matchingAccounts, pay)
    const note = `${rows.length - 1} ${listNoun} · ${kind === 'csv' ? 'CSV' : 'Excel'}`
    if (kind === 'csv') startTask(fileTask('Export personnel', note, `personnel-${viewName}.csv`, 'text/csv;charset=utf-8', () => personnelCsv(rows)))
    else startTask(fileTask('Export personnel', note, `personnel-${viewName}.xlsx`, XLSX_TYPE, () => workbook([{ name: 'Personnel', rows }])))
  }

  const listNoun = groupFilter ? 'members' : tab === 'field' ? 'field workers' : tab === 'system' ? 'system users' : 'archived'
  const countLine = needle ? `Showing ${matchCount} of ${total} ${listNoun}` : `${total} ${listNoun}`

  const rateCell = (p: Person) => {
    if (p.hourly_rate == null && p.next_rate == null) return <span className={styles.muted}>{p.membership_id ? 'Not paid hourly' : 'No rate set'}</span>
    return (
      <>
        {p.hourly_rate != null ? money(Number(p.hourly_rate)) + '/h' : <span className={styles.muted}>No rate yet</span>}
        {p.next_rate != null && (
          <span className={styles.next}>
            {money(Number(p.next_rate))}/h from {dmy(p.next_rate_from)}
          </span>
        )}
      </>
    )
  }

  // Format a date as relative time (e.g., "6 min ago", "3 days ago")
  const relativeTime = (iso: string | null | undefined): string => {
    if (!iso) return '-'
    const date = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    return dmy(iso)
  }

  // Header actions (handoff §7b: Import, Export, Label printing, Add worker, Hide help, cog)
  const headerActions = (
    <>
      {!loading && <AsAt at={asAt} />}
      {mayImport && (
        <button type="button" className={headerStyles.btn} aria-label="Import" title="Import personnel" onClick={() => setImporting(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          Import
        </button>
      )}
      <div className={pa.dropWrap}>
        <button type="button" className={headerStyles.btn} aria-label="Export" aria-haspopup="menu" aria-expanded={exportMenu} onClick={() => setExportMenu((v) => !v)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Export
        </button>
        {exportMenu && (
          <div className={`${pa.dropMenu} ${styles.menuRight}`} role="menu" aria-label="Export">
            <button type="button" role="menuitem" className={pa.dropOpt} onClick={() => exportAs('csv')}>
              <span>CSV</span>
            </button>
            <button type="button" role="menuitem" className={pa.dropOpt} onClick={() => exportAs('xlsx')}>
              <span>Excel</span>
            </button>
            <p className={styles.menuNote}>
              The {matchCount} {matchCount === 1 ? 'row' : 'rows'} in this view, every page, with its search and filter.
            </p>
          </div>
        )}
      </div>
      <button
        type="button"
        className={headerStyles.btn}
        aria-label="Print QR labels"
        disabled={onlyArchived}
        title={
          onlyArchived
            ? 'Archived people cannot be given labels'
            : labelable.length
              ? `Opens Label printing with the ${labelable.length === 1 ? 'selected person' : `${labelable.length} selected people`}`
              : 'Opens Label printing for Worker ID labels. Tick people first to bring them with you.'
        }
        onClick={printLabels}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <polyline points="6 9 6 2 18 2 18 9" />
          <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
          <rect x="6" y="14" width="12" height="8" />
        </svg>
        Print QR labels
      </button>
      {tab === 'system' && !groupFilter
        ? mayInvite && (
            <button type="button" className={`${headerStyles.btn} ${headerStyles.btnPrimary}`} onClick={() => setInviting({})}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Invite system user
            </button>
          )
        : mayAdd && (
            <button type="button" className={`${headerStyles.btn} ${headerStyles.btnPrimary}`} onClick={() => setPanel({ person: null })}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
                <path d="M12 5v14M5 12h14" />
              </svg>
              Add worker
            </button>
          )}
      <button type="button" className={headerStyles.btnHelp} onClick={() => setHelp(!help)}>
        {help ? 'Hide help' : 'How Personnel works'}
      </button>
      {mayCog && (
        <button
          type="button"
          className={headerStyles.iconBtn}
          aria-label="Personnel settings"
          title={mayRate ? 'Personnel settings: the default rate and the job roles on offer' : 'Personnel settings: the job roles on offer'}
          onClick={openCog}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
          </svg>
        </button>
      )}
    </>
  )

  return (
    <div className={styles.container}>
      <PageHeader
        title="Personnel"
        actions={headerActions}
        tabs={<PairTabs label="Personnel and groups" tabs={PEOPLE_TABS} current="Personnel" />}
      />

      <PageBody className={styles.body}>
        {help && (
          <div className={`${pa.helpGrid} ${styles.helpWrap}`} data-testid="personnel-help">
            {HELP.map(([h, body]) => (
              <div key={h} className={pa.helpCard}>
                <span>{h}</span>
                <p>{body}</p>
              </div>
            ))}
            <button type="button" className={`${pa.closeX} ${styles.helpX}`} title="Hide help" aria-label="Hide help" onClick={() => setHelp(false)}>
              ×
            </button>
          </div>
        )}

        {error && !loading && (
          <div className={styles.error} role="alert">
            <p>The list could not be refreshed: {error}. What is shown may be out of date.</p>
            <button type="button" className={pa.ghostBtn} onClick={() => load()}>
              Retry
            </button>
          </div>
        )}

        {cogError && (
          <div className={styles.error}>
            <p>{cogError}</p>
          </div>
        )}

        {groupFilter && (
          <div className={styles.filterBar} role="status">
            <span>
              Showing the members of <strong>{filterName}</strong> ({inView.length})
            </span>
            <button type="button" className={styles.filterClear} onClick={() => setParams({})}>
              Show everyone
            </button>
          </div>
        )}

        <div className={styles.toolRow}>
          <div className={pa.searchWrap}>
            <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" className={pa.searchIcon} aria-hidden="true">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input className={pa.search} type="search" placeholder="Search name, ID or email…" aria-label="Search personnel" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className={pa.dropWrap}>
            <button type="button" className={pa.dropBtn} aria-haspopup="menu" aria-expanded={groupMenu} onClick={() => setGroupMenu((v) => !v)}>
              <span className={pa.dropLabel}>Group</span>
              <span className={pa.dropValue}>{groupFilter ? filterName : 'All groups'}</span>
            </button>
            {groupMenu && (
              <div className={pa.dropMenu} role="menu" aria-label="Group">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={!groupFilter}
                  className={!groupFilter ? pa.dropOptOn : pa.dropOpt}
                  onClick={() => {
                    setGroupMenu(false)
                    setParams({})
                  }}
                >
                  <span>All groups</span>
                </button>
                {groupOptions.map(([id, name]) => (
                  <button
                    key={id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={groupFilter === id}
                    className={groupFilter === id ? pa.dropOptOn : pa.dropOpt}
                    onClick={() => {
                      setGroupMenu(false)
                      setParams({ group: id })
                    }}
                  >
                    <span>{name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className={pa.dropWrap}>
            <button
              type="button"
              className={filterCount || filtersOpen ? styles.filtersBtnOn : styles.filtersBtn}
              aria-expanded={filtersOpen}
              aria-controls="personnel-filters"
              onClick={toggleFilters}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
                <path d="M4 6h16M7 12h10M10 18h4" />
              </svg>
              Filters
              {filterCount > 0 && <span className={styles.filtersCount}>{filterCount}</span>}
            </button>
            {filtersOpen && (
              <div id="personnel-filters" className={styles.filtersPop} role="group" aria-label="Filters">
                <div className={styles.filtersSection}>
                  <span className={styles.filtersLabel}>Role</span>
                  {roleOptions.length ? (
                    <div className={styles.chips}>
                      {roleOptions.map((r) => {
                        const on = roleFilter.includes(r)
                        return (
                          <button
                            key={r}
                            type="button"
                            aria-pressed={on}
                            className={on ? styles.chipOn : styles.chip}
                            onClick={() => setRoleFilter(on ? roleFilter.filter((x) => x !== r) : [...roleFilter, r])}
                          >
                            {r}
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <span className={styles.filtersNone}>Nobody has a job role yet.</span>
                  )}
                </div>
                {pay && (
                  <div className={styles.filtersSection}>
                    <span className={styles.filtersLabel}>Rate</span>
                    <div className={styles.chips}>
                      {(
                        [
                          ['floor', 'At the floor'],
                          ['above', 'Above the floor'],
                        ] as const
                      ).map(([k, label]) => (
                        <button
                          key={k}
                          type="button"
                          aria-pressed={rateFilter === k}
                          className={rateFilter === k ? styles.chipOn : styles.chip}
                          onClick={() => setRateFilter(rateFilter === k ? '' : k)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className={styles.filtersFoot}>
                  <span className={styles.filtersResult}>
                    {matching.length === inView.length ? 'Showing everyone' : `${matching.length} ${matching.length === 1 ? 'person matches' : 'people match'}`}
                  </span>
                  <button
                    type="button"
                    className={styles.filtersClear}
                    onClick={() => {
                      setRoleFilter([])
                      setRateFilter('')
                    }}
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {selectedPeople.length > 0 && (
          <div className={styles.selBar} aria-label="Selection">
            <span className={styles.selLabel}>
              {selectedPeople.length} {selectedPeople.length === 1 ? 'profile' : 'profiles'} selected
            </span>
            {selectedPeople.length < matching.length && (
              <button type="button" className={styles.selLink} onClick={() => setSelected(new Set(matching.map((p) => p.id)))}>
                Select all {matching.length} matching
              </button>
            )}
            <button type="button" className={styles.selClear} onClick={() => setSelected(new Set())}>
              Clear
            </button>
            <span className={styles.selActions}>
            {(mayEdit || selectedPeople.length === 1) && (
              <button type="button" className={styles.selBtn} onClick={editSelection}>
                {selectedPeople.length === 1 ? (mayEdit ? 'Edit' : 'Open') : `Bulk edit ${selectedPeople.length}`}
              </button>
            )}
            {mayAdd && !selectedPeople.every((p) => p.archived) &&
              (archiveAsk ? (
                <span className={styles.selAsk} role="alert">
                  <span>
                    Archive {selectedPeople.length === 1 ? fullName(selectedPeople[0]) : `${selectedPeople.length} people`}? Nothing is deleted, so history stays intact.
                    {selectedPeople.some((p) => p.membership_id && !p.archived) &&
                      (hasPower(mine, 'manage_sign_in')
                        ? ' Anyone selected who signs in to CONTROL loses that sign-in here; restoring them does not give it back.'
                        : ' Someone selected signs in to CONTROL, so only an ADMIN can archive them.')}
                  </span>
                  <button type="button" className={styles.selBtn} disabled={archiveBusy} onClick={() => setArchiveAsk(false)}>
                    Keep
                  </button>
                  <PendingButton className={styles.selBtn} busy={archiveBusy} pending="Archiving…" onClick={archiveSelection}>
                    {`Archive ${selectedPeople.length}`}
                  </PendingButton>
                </span>
              ) : (
                <button type="button" className={styles.selBtn} onClick={() => setArchiveAsk(true)}>
                  Archive
                </button>
              ))}
            {/* Merge takes exactly two records (handoff 836-837). */}
            {mayAdd && (
            <button
              type="button"
              className={styles.selMerge}
              disabled={selectedPeople.length !== 2}
              title={selectedPeople.length !== 2 ? 'Tick exactly two profiles to merge' : undefined}
              onClick={() => setMerging([selectedPeople[0], selectedPeople[1]])}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M7 3v6a4 4 0 0 0 4 4h6" />
                <path d="M17 3v6a4 4 0 0 1-4 4H7" />
                <path d="M12 13v8" />
              </svg>
              Merge profiles
            </button>
            )}
            </span>
            <Failed text={archiveError && `${archiveError} Nobody was archived.`} />
          </div>
        )}

        <div className={styles.tableCard}>
          {!groupFilter && (
            <div className={styles.cardTabs}>
              <div className={styles.cardTabsTrack} role="tablist" aria-label="Lists">
                {(Object.keys(TAB_LABEL) as Tab[]).map((t) => (
                  <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? styles.cardTabOn : styles.cardTab} onClick={() => setTab(t)}>
                    <span>{TAB_LABEL[t]}</span>
                    <span className={tab === t ? styles.cardTabCount : styles.cardTabCountOff}>{counts[t]}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!loading && shown.length + extraAccounts.length === 0 ? (
            <div className={styles.emptyState}>
              <p>
                {needle
                  ? 'Nobody matches that search.'
                  : groupFilter
                    ? 'Nobody is in this group.'
                    : tab === 'field'
                      ? 'No field workers yet. Add one, or they arrive from the Dataphyll Crew App and imports.'
                      : tab === 'system'
                        ? 'Nobody signs in to CONTROL from this workspace yet.'
                        : 'Nobody is archived.'}
              </p>
            </div>
          ) : (
            <div className={styles.table}>
              <table>
                <thead>
                  <tr>
                    <th className={styles.checkCol}>
                      <PageCheckbox checked={pageIds.length > 0 && pageSelected === pageIds.length} partial={pageSelected > 0 && pageSelected < pageIds.length} disabled={pageIds.length === 0 || loading} onChange={togglePage} />
                    </th>
                    {th('name', 'Name · role · group')}
                    {th('employee_id', 'Emp ID')}
                    {pay && th('rate', 'Rate', styles.money)}
                    <th>Connections</th>
                    {th('contact', 'Contact')}
                    {th('last_active', 'Last active')}
                  </tr>
                </thead>
                {loading ? (
                  <SkeletonTableRows cols={pay ? 7 : 6} rows={8} label="Loading personnel" />
                ) : (
                  <tbody>
                    {shown.map((p) => (
                      <tr key={p.id} className={`${styles.clickable} ${selected.has(p.id) ? styles.rowOn : ''}`} {...openableRow(() => setPanel({ person: p }))}>
                        {/* The checkbox selects, the row opens the record: two targets
                            in one row, so the cell stops the click (handoff 702-703). */}
                        <td className={styles.checkCol} onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" aria-label={`Select ${fullName(p)}`} checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
                        </td>
                        <td>
                          <div className={styles.person}>
                            <PersonAvatar id={p.id} photoAt={p.photo_at} initials={initialsOf(p)} className={p.archived ? styles.avatarMuted : p.membership_id ? styles.avatarSystem : styles.avatar} />
                            <div className={styles.nameText}>
                              <div className={styles.nameLine}>
                                <span>{fullName(p)}</span>
                                {dupes.has(p.id) && (
                                  <span className={styles.dupeWrap} onClick={(e) => e.stopPropagation()}>
                                    <button
                                      type="button"
                                      className={styles.dupeChip}
                                      aria-haspopup="menu"
                                      aria-expanded={dupeMenu === p.id}
                                      title="Possible duplicate: someone else here has the same name"
                                      onClick={() => {
                                        setDupeError(null)
                                        setDupeMenu((x) => (x === p.id ? null : p.id))
                                      }}
                                    >
                                      Dupe?
                                    </button>
                                    {dupeMenu === p.id && (
                                      <div className={`${pa.dropMenu} ${styles.dupeMenu}`} role="menu" aria-label={`Possible duplicates of ${fullName(p)}`}>
                                        {(dupes.get(p.id) || []).map((otherId) => {
                                          const other = personnel.find((x) => x.id === otherId)
                                          if (!other) return null
                                          return (
                                            <div key={otherId} className={styles.dupeItem}>
                                              <p className={styles.menuNote}>
                                                Same name as {fullName(other)}
                                                {[other.employee_id && `ID ${other.employee_id}`, other.email].filter(Boolean).length
                                                  ? ` (${[other.employee_id && `ID ${other.employee_id}`, other.email].filter(Boolean).join(', ')})`
                                                  : ''}
                                                .
                                              </p>
                                              {mayAdd ? (
                                                <>
                                                  <button
                                                    type="button"
                                                    role="menuitem"
                                                    className={pa.dropOpt}
                                                    disabled={dupeBusy}
                                                    onClick={() => {
                                                      setDupeMenu(null)
                                                      setMerging([p, other])
                                                    }}
                                                  >
                                                    <span>Merge profiles…</span>
                                                  </button>
                                                  <button type="button" role="menuitem" className={pa.dropOpt} disabled={dupeBusy} onClick={() => dismissDupe(p, other)}>
                                                    <span>{dupeBusy ? 'Saving…' : 'Not the same person'}</span>
                                                  </button>
                                                </>
                                              ) : (
                                                <p className={styles.menuNote}>An ADMIN can merge them or record that they are different people.</p>
                                              )}
                                            </div>
                                          )
                                        })}
                                        <Failed text={dupeError} />
                                      </div>
                                    )}
                                  </span>
                                )}
                              </div>
                              <div className={styles.sub}>
                                {p.membership_id && p.permission_set && <span className={styles.permChip}>{p.permission_set}</span>}
                                {p.membership_id && inviteBits(accountById.get(p.membership_id))}
                                {p.membership_id && actAsBits(accountById.get(p.membership_id))}
                                {p.job_roles && p.job_roles.length > 0 && <span>{p.job_roles.join(', ')}</span>}
                                {p.group_name && <span className={styles.groupChip}>{p.group_name}</span>}
                                {p.default_team_name && <span>{p.default_team_name}</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{p.employee_id || '-'}</td>
                        {pay && <td className={styles.money}>{rateCell(p)}</td>}
                        <td>
                          <Connections p={p} />
                        </td>
                        <td>
                          <ContactCell p={p} />
                        </td>
                        <td className={styles.lastActive}>{relativeTime(p.last_active)}</td>
                      </tr>
                    ))}
                    {extraAccounts.map((a) => (
                      <tr key={a.id} title={a.invite_status ? undefined : 'This sign-in has no person record, so there is nothing of theirs to edit here'}>
                        <td className={styles.checkCol} />
                        <td>
                          <div className={styles.person}>
                            <span className={styles.avatarMuted} aria-hidden="true">
                              {(a.name || a.email).charAt(0).toUpperCase()}
                            </span>
                            <div className={styles.nameText}>
                              <div className={styles.nameLine}>
                                <span>{a.name || a.email}</span>
                              </div>
                              <div className={styles.sub}>
                                <span className={styles.permChip}>{a.permission_set}</span>
                                {inviteBits(a)}
                                {actAsBits(a)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>-</td>
                        {pay && (
                          <td>
                            <span className={styles.muted}>Not paid hourly</span>
                          </td>
                        )}
                        <td>
                          {shownClientApps(a.client_apps).length ? (
                            <div className={styles.connections}>
                              <ClientAppBadges apps={shownClientApps(a.client_apps)} />
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{a.email}</td>
                        <td>-</td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          )}

          {!loading && (
            <nav className={styles.pager} aria-label="Pages">
              <span data-testid="count-line">{countLine}</span>
              <div className={styles.pagerButtons}>
                <button type="button" className={pa.ghostBtn} disabled={at === 0} onClick={() => setPage(at - 1)}>
                  Previous
                </button>
                <span className={styles.pageInfo}>
                  {at * PAGE_SIZE + 1}–{Math.min((at + 1) * PAGE_SIZE, matching.length)} of {matching.length}
                </span>
                <button type="button" className={pa.ghostBtn} disabled={at >= pages - 1} onClick={() => setPage(at + 1)}>
                  Next
                </button>
              </div>
            </nav>
          )}
        </div>

        {panel && (
          <PersonRecordPanel
            person={panel.person}
            onClose={() => setPanel(null)}
            onOpenGroups={() => {
              setPanel(null)
              navigate('/groups')
            }}
            onSaved={(msg) => {
              setPanel(null)
              setToast(msg)
              load(true)
            }}
            onPhotoChanged={() => load(true)}
            onAccessChanged={() => load(true)}
          />
        )}
        {inviting && (
          <InviteDialog
            user={user}
            initial={inviting.result}
            onClose={() => setInviting(null)}
            onInvited={() => load(true)}
          />
        )}
        {cog && (
          <PersonnelSettingsModal
            user={user}
            showRate={mayRate}
            current={cog.current}
            withoutRate={withoutRate}
            onClose={() => setCog(null)}
            onSaved={(msg) => {
              setCog(null)
              setToast(msg)
            }}
          />
        )}
        {merging && (
          <MergeDialog
            user={user}
            people={merging}
            onClose={() => setMerging(null)}
            onMerged={(msg) => {
              setMerging(null)
              setSelected(new Set())
              setToast(msg)
              load(true)
            }}
          />
        )}
        {importing && (
          <ImportPanel
            user={user}
            people={personnel}
            onClose={() => setImporting(false)}
            onDone={(msg) => {
              setImporting(false)
              setToast(msg)
              load(true)
            }}
          />
        )}
        {bulk && (
          <BulkEditPanel
            people={bulk}
            onClose={() => setBulk(null)}
            onSaved={(msg) => {
              setBulk(null)
              setToast(msg)
              load(true)
            }}
          />
        )}
      </PageBody>
      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  )
}
